// src/components/AddUserForm.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Paper,
  Grid,
  Alert,
  Card,
  CardHeader,
  CardContent,
  Snackbar,
  Stack,
  Chip,
  Avatar,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import SecurityRoundedIcon from "@mui/icons-material/SecurityRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import PhoneIphoneRoundedIcon from "@mui/icons-material/PhoneIphoneRounded";
import LockResetRoundedIcon from "@mui/icons-material/LockResetRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import { Autocomplete } from "@mui/material";
import { useAuthContext } from "../context/AuthContext";
import { fetchData } from "../api/client";
import { useUserFull } from "../hooks/useUserFull";
import { useTranslation } from "react-i18next";

/* ===================== Helpers ===================== */
const onlyDigits = (s) => String(s || "").replace(/\D+/g, "");
const readSelectedCompaniesFromSession = () => {
  try {
    const raw = sessionStorage.getItem("selectedCompanies");
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.map((v) => Number(v)).filter(Boolean) : [];
  } catch {
    return [];
  }
};
const isMissingDate = (v) =>
  v == null ||
  v === "" ||
  v === 0 ||
  v === "0" ||
  v === "0000-00-00" ||
  v === "0000-00-00 00:00:00";

const toDateOrNull = (v) => {
  if (isMissingDate(v)) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

const fmtRelativePtBR = (date) => {
  const rtf = new Intl.RelativeTimeFormat("pt-BR", { numeric: "auto" });
  const now = new Date();
  let diff = (date.getTime() - now.getTime()) / 1000;

  const abs = Math.abs(diff);
  if (abs < 60) return rtf.format(Math.round(diff), "second");
  diff /= 60;
  if (Math.abs(diff) < 60) return rtf.format(Math.round(diff), "minute");
  diff /= 60;
  if (Math.abs(diff) < 24) return rtf.format(Math.round(diff), "hour");
  diff /= 24;
  if (Math.abs(diff) < 30) return rtf.format(Math.round(diff), "day");
  diff /= 30;
  if (Math.abs(diff) < 12) return rtf.format(Math.round(diff), "month");
  diff /= 12;
  return rtf.format(Math.round(diff), "year");
};

const fmtLastAccessLabel = (loading, lastAccess, createdAt) => {
  if (loading) return "verificando…";
  const lastAccessDate = toDateOrNull(lastAccess);
  const createdAtDate = toDateOrNull(createdAt);
  if (!lastAccessDate) {
    const rel = createdAtDate ? fmtRelativePtBR(createdAtDate) : null;
    return rel ? `Aguardando primeiro acesso • ${rel}` : "Aguardando primeiro acesso";
  }
  return fmtRelativePtBR(lastAccessDate);
};

const getTooltip = (lastAccess, createdAt) => {
  const lastAccessDate = toDateOrNull(lastAccess);
  const createdAtDate = toDateOrNull(createdAt);
  const d = lastAccessDate || createdAtDate;
  return d ? d.toLocaleString("pt-BR") : "";
};

const fmtDateTime = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  return isNaN(d.getTime()) ? "—" : d.toLocaleString("pt-BR");
};
const firstOr = (a, b) => (a !== undefined && a !== null && a !== "" ? a : b);

/** Máscara de telefone BR dinâmico */
function maskPhoneBR(s) {
  const d = onlyDigits(s).slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`; // 10 dígitos
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7, 11)}`; // 11 dígitos
}
const isValidPhoneBR = (s) => {
  const d = onlyDigits(s);
  return d.length === 10 || d.length === 11;
};

/** Máscara para ID interno: A–Z 0–9 . _ - (sem espaços) */
function maskInternalId(s) {
  return String(s || "")
    .toUpperCase()
    .replace(/\s+/g, "")
    .replace(/[^A-Z0-9._-]/g, "")
    .slice(0, 24);
}
const isValidInternalId = (s) => /^[A-Z0-9._-]{1,24}$/.test(s);

/** E-mail */
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;

/** Desempacota respostas { message, data } ou { data:{ data: ... } } */
function unwrap(res) {
  let d = res?.data ?? res;
  if (d && typeof d === "object" && "data" in d) d = d.data;
  if (d && typeof d === "object" && "data" in d) d = d.data;
  return d;
}

/* ===================== Componente ===================== */
export default function AddUserForm({ user, onClose, onSaved, isLoading }) {
  const { userData } = useAuthContext();
  const { t } = useTranslation("settings");
  const isEdit = !!user?.id;

  const [form, setForm] = useState(() => ({
    id: user?.id,
    nome: user?.nome || "",
    email: user?.email || "",
    senha: "",
    status: Number.isFinite(user?.status) ? Number(user.status) : 1,
    celular: user?.celular ? maskPhoneBR(user.celular) : "",
    id_cargo: user?.id_cargo || "",
    id_interno: user?.id_interno ? maskInternalId(user.id_interno) : "",
    id_usuario: user?.id_usuario || userData?.id,
    id_usuario_ultima_alteracao: userData?.id,
    data_ultimo_acesso: user?.data_ultimo_acesso || user?.lastAccess || null,
    data_cadastro: user?.data_cadastro || user?.createdAt || null,
    data_ultima_alteracao: user?.data_ultima_alteracao || user?.updatedAt || null,
  }));

  const [positions, setPositions] = useState([]);
  const [positionsLoading, setPositionsLoading] = useState(false);

  // loading submit
  const [saving, setSaving] = useState(false);

  // toques/campos "touched" para UX
  const [touched, setTouched] = useState({});

  const set = (patch) => {
    if (typeof patch === "function") setForm((prev) => ({ ...prev, ...patch(prev) }));
    else setForm((prev) => ({ ...prev, ...patch }));
  };

  const { data: full, loading: fullLoading, error: fullError, reload: reloadFull } = useUserFull(form.id);

  // Reset quando troca de usuário
  useEffect(() => {
    setForm({
      id: user?.id,
      nome: user?.nome || "",
      email: user?.email || "",
      senha: "",
      status: Number.isFinite(user?.status) ? Number(user.status) : 1,
      celular: user?.celular ? maskPhoneBR(user.celular) : "",
      id_cargo: user?.id_cargo || "",
      id_interno: user?.id_interno ? maskInternalId(user.id_interno) : "",
      id_usuario: user?.id_usuario || userData?.id,
      id_usuario_ultima_alteracao: userData?.id,
      data_ultimo_acesso: user?.data_ultimo_acesso || user?.lastAccess || null,
      data_cadastro: user?.data_cadastro || user?.createdAt || null,
      data_ultima_alteracao: user?.data_ultima_alteracao || user?.updatedAt || null,
    });
    setTouched({});
    setSelectedProjects([]);
    setProjQuery("");
    setProjectOptions([]);
    setLogs([]);
    setErrorMessage(null);
    setAclError(null);
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Hidratar do /users/:id ao abrir */
  useEffect(() => {
    if (!isEdit || !user?.id) return;
    let alive = true;
    (async () => {
      try {
        const res = await fetchData(`/users/${user.id}`, "GET");
        const u = unwrap(res);
        if (!alive || !u) return;
        set((p) => ({
          ...p,
          nome: firstOr(u.nome, p.nome),
          email: firstOr(u.email, p.email),
          id_cargo: firstOr(u.id_cargo, p.id_cargo),
          id_interno: maskInternalId(firstOr(u.id_interno, p.id_interno)),
          status: Number.isFinite(u.status) ? Number(u.status) : p.status,
          celular: maskPhoneBR(firstOr(u.celular, p.celular)),
          data_ultimo_acesso: firstOr(u.data_ultimo_acesso, p.data_ultimo_acesso),
        }));
      } catch {}
    })();
    return () => {
      alive = false;
    };
  }, [isEdit, user?.id]);

  // Quando hook trouxer detalhe
  useEffect(() => {
    if (!full) return;
    if (full.user) {
      set((p) => ({
        ...p,
        nome: firstOr(full.user.nome, p.nome),
        email: firstOr(full.user.email, p.email),
        id_cargo: firstOr(full.user.id_cargo, p.id_cargo),
        id_interno: maskInternalId(firstOr(full.user.id_interno, p.id_interno)),
        status: Number.isFinite(full.user.status) ? Number(full.user.status) : p.status,
        celular: maskPhoneBR(firstOr(full.user.celular, p.celular)),
        data_ultimo_acesso: firstOr(full.user.data_ultimo_acesso, p.data_ultimo_acesso),
      }));
    }
    if (Array.isArray(full.projects)) {
      setSelectedProjects(
        full.projects.map((prj) => ({ id: prj.id, nome: prj.nome || "", codigo_interno: prj.codigo_interno || "" }))
      );
    }
    if (Array.isArray(full.logs)) setLogs(full.logs);
  }, [full]); // eslint-disable-line react-hooks/exhaustive-deps

  // Carregar cargos (positions)
  useEffect(() => {
    let alive = true;
    setPositionsLoading(true);
    fetchData(`/positions?limit=200`, "GET")
      .then((resp) => {
        const data = unwrap(resp);
        const list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
        if (alive) setPositions(list);
      })
      .catch(() => alive && setPositions([]))
      .finally(() => alive && setPositionsLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const [errors, setErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  /* ---------- ACL (projetos) ---------- */
  const [aclLoading, setAclLoading] = useState(false);
  const [aclSaving, setAclSaving] = useState(false);
  const [aclError, setAclError] = useState(null);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [projectOptions, setProjectOptions] = useState([]);
  const [projQuery, setProjQuery] = useState("");
  const [projLoading, setProjLoading] = useState(false);

  // Default ACL no "novo usuário": empresas selecionadas no CompanySelector (sessionStorage)
  useEffect(() => {
    if (isEdit) return; // em edição, a ACL vem do backend
    if (selectedProjects.length) return; // se já foi mexido, não sobrescreve

    const ids = readSelectedCompaniesFromSession();
    if (!ids.length) return;

    let alive = true;
    (async () => {
      try {
        // Busca um lote para resolver nomes. Se alguma não vier, mantém placeholder.
        const resp = await fetchData(`/companies?limit=300`, "GET");
        const data = unwrap(resp);
        const rows = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
        const byId = new Map(rows.map((c) => [Number(c.id), c]));

        const resolved = ids.map((id) => {
          const c = byId.get(Number(id));
          return {
            id: Number(id),
            nome: c?.nome || `Empresa #${id}`,
            codigo_interno: c?.codigo_interno || "",
          };
        });
        if (alive) setSelectedProjects(resolved);
      } catch {
        if (alive) {
          setSelectedProjects(ids.map((id) => ({ id: Number(id), nome: `Empresa #${id}`, codigo_interno: "" })));
        }
      }
    })();

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit]);

  // Fallback ACL
  useEffect(() => {
    if (!form.id) return;
    if (Array.isArray(full?.projects)) return;
    let alive = true;
    setAclLoading(true);
    setAclError(null);
    fetchData(`/users/${form.id}/acl`, "GET")
      .then((resp) => {
        if (!alive) return;
        const data = unwrap(resp);
        const list = Array.isArray(data?.projects) ? data.projects : Array.isArray(data) ? data : [];
        setSelectedProjects(list.map((p) => ({ id: p.id, nome: p.nome || "", codigo_interno: p.codigo_interno || "" })));
      })
      .catch((e) => alive && setAclError(e?.response?.data?.message || "Falha ao carregar permissões do usuário."))
      .finally(() => alive && setAclLoading(false));
    return () => {
      alive = false;
    };
  }, [form.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Opções de projetos
  useEffect(() => {
    let alive = true;
    const term = String(projQuery || "").trim();
    const t = setTimeout(() => {
      setProjLoading(true);
      const url = term ? `/companies?search=${encodeURIComponent(term)}&limit=20` : `/companies?limit=20`;
      fetchData(url, "GET")
        .then((resp) => {
          if (!alive) return;
          const data = unwrap(resp);
          const rows = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
          setProjectOptions(rows.map((p) => ({ id: p.id, nome: p.nome || "", codigo_interno: p.codigo_interno || "" })));
        })
        .catch(() => alive && setProjectOptions([]))
        .finally(() => alive && setProjLoading(false));
    }, 300);
    return () => {
      clearTimeout(t);
      alive = false;
    };
  }, [projQuery]);

  /* ---------- Logs ---------- */
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState(null);

  useEffect(() => {
    if (!form.id) return;
    if (Array.isArray(full?.logs)) return;
    let alive = true;
    setLogsLoading(true);
    setLogsError(null);
    fetchData(`/users/${form.id}/logs?limit=20`, "GET")
      .then((resp) => {
        if (!alive) return;
        const data = unwrap(resp);
        const items = Array.isArray(data) ? data : Array.isArray(data?.logs) ? data.logs : [];
        setLogs(items);
      })
      .catch((e) => alive && setLogsError(e?.response?.data?.message || "Falha ao carregar atividades do usuário."))
      .finally(() => alive && setLogsLoading(false));
    return () => {
      alive = false;
    };
  }, [form.id]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ---------- Validação ---------- */
  const computeErrors = (state) => {
    const errs = {};
    const nome = String(state.nome || "").trim();
    const email = String(state.email || "").trim();
    const celular = String(state.celular || "");
    const idInterno = String(state.id_interno || "");

    if (!nome) errs.nome = "Campo obrigatório";
    else if (nome.length < 2) errs.nome = "Informe ao menos 2 caracteres";

    if (!email) errs.email = "Campo obrigatório";
    else if (!emailRegex.test(email)) errs.email = "E-mail inválido";

    if (celular && !isValidPhoneBR(celular)) errs.celular = "Informe um celular válido";

    if (idInterno && !isValidInternalId(idInterno))
      errs.id_interno = "Use A–Z, 0–9, ., _ ou - (até 24)";

    return errs;
  };

  const validate = () => {
    const errs = computeErrors(form);
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const touch = (field) => setTouched((t) => ({ ...t, [field]: true }));

  /* ---------- Submit ---------- */
  const save = async (e) => {
    e.preventDefault?.();
    // força mostrar erros
    setTouched({ nome: true, email: true, celular: true, id_interno: true });
    if (!validate()) return;

    if (!form.id && selectedProjects.length === 0) {
      setSnack({
        open: true,
        severity: "error",
        message: "Selecione ao menos uma empresa nas permissões antes de criar o usuário.",
      });
      return;
    }

    setSaving(true);
    try {
      setErrorMessage(null);

      const payload = {
        id: form.id,
        nome: form.nome.trim(),
        email: form.email.trim(),
        status: Number(form.status) === 1 ? 1 : 0,
        celular: onlyDigits(form.celular) || null, // envia só dígitos
        id_usuario: form.id_usuario || userData?.id || null,
        id_usuario_ultima_alteracao: userData?.id || null,
        id_cargo: form.id_cargo ? Number(form.id_cargo) : null,
        id_interno: form.id_interno || null,
      };

      let res;
      let createdId = null;
      if (form.id) {
        res = await fetchData(`/users/${form.id}`, "PUT", payload);
      } else {
        res = await fetchData(`/users`, "POST", payload);
        createdId = res?.data?.id;
        if (createdId) await fetchData(`/users/${createdId}/send-invite`, "POST");

        // Aplica ACL imediatamente após criar (evita "usuário sem permissão")
        const projects = selectedProjects.map((p) => Number(p.id)).filter(Boolean);
        if (createdId && projects.length) {
          try {
            await fetchData(`/users/${createdId}/acl`, "PUT", { projects });
          } catch {
            // Não impede o usuário de existir; mas sinaliza que precisa revisar
            setSnack({
              open: true,
              severity: "warning",
              message: "Usuário criado, mas falhou ao aplicar permissões. Revise as permissões após salvar.",
            });
          }
        }
      }

      setSnack({ open: true, message: "Usuário salvo com sucesso.", severity: "success" });
      onSaved?.(res);
      if (form.id) reloadFull?.();
    } catch (err) {
      setErrorMessage(err?.response?.data?.message || "Erro ao salvar o usuário.");
    } finally {
      setSaving(false);
    }
  };

  /* ---------- Ações: senha & ACL ---------- */
  const handleSendInvite = async () => {
    if (!form.id) return;
    await fetchData(`/users/${form.id}/send-invite`, "POST");
    setSnack({ open: true, message: "Convite enviado por e-mail.", severity: "success" });
  };

  const handleSendReset = async () => {
    if (!form.id) return;
    await fetchData(`/users/${form.id}/send-reset`, "POST");
    setSnack({ open: true, message: "E-mail de redefinição enviado.", severity: "success" });
  };

  const handleSaveAcl = async () => {
    if (!form.id) {
      setSnack({ open: true, severity: "error", message: "Salve o usuário antes de configurar permissões." });
      return;
    }
    try {
      setAclSaving(true);
      setAclError(null);
      const projects = selectedProjects.map((p) => Number(p.id)).filter(Boolean);
      await fetchData(`/users/${form.id}/acl`, "PUT", { projects });
      setSnack({ open: true, severity: "success", message: "Permissões salvas com sucesso." });
      reloadFull?.();
    } catch (e) {
      setAclError(e?.response?.data?.message || "Erro ao salvar permissões.");
      setSnack({ open: true, severity: "error", message: "Falha ao salvar permissões." });
    } finally {
      setAclSaving(false);
    }
  };

  /* ---------- UI ---------- */
  const showError = (name) => Boolean((touched[name] || saving) && errors[name]);
  const help = (name) => (showError(name) ? errors[name] : " ");

  return (
    <Paper elevation={3} component="form" onSubmit={save} sx={{ p: 2.5, width: "95%", m: "0 auto" }}>
      <Typography variant="h6" sx={{ mb: 1.5 }}>
        {form.id ? t("addUserForm.titleEdit") : t("addUserForm.titleAdd")}
      </Typography>

      {(errorMessage || fullError) && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{errorMessage || fullError}</Alert>
        </Box>
      )}

      {/* Identificação & Status */}
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardHeader
          title={t("addUserForm.identityTitle")}
          subheader={t("addUserForm.identitySubheader")}
        />
        <CardContent sx={{ pt: 0 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label={t("addUserForm.nameLabel")}
                value={form.nome}
                onChange={(e) => set({ nome: e.target.value })}
                onBlur={() => {
                  touch("nome");
                  setErrors((prev) => ({ ...prev, ...computeErrors({ ...form, nome: form.nome }) }));
                }}
                fullWidth
                autoComplete="name"
                error={showError("nome")}
                helperText={help("nome")}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                select
                label={t("addUserForm.statusLabel")}
                name="status"
                value={String(form.status ?? "")}
                onChange={(e) => set({ status: Number(e.target.value) })}
                fullWidth
                SelectProps={{ native: true }}
              >
                <option value="1">{t("shared.active")}</option>
                <option value="0">{t("shared.inactive")}</option>
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                select
                label={t("addUserForm.roleLabel")}
                value={form.id_cargo || ""}
                onChange={(e) => set({ id_cargo: e.target.value ? Number(e.target.value) : null })}
                fullWidth
                SelectProps={{ native: true }}
              >
                <option value="">{t("addUserForm.selectOption")}</option>
                {positionsLoading && <option disabled>{t("shared.loading")}</option>}
                {(positions || []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nome}
                  </option>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label={t("addUserForm.internalIdLabel")}
                placeholder={t("addUserForm.internalIdPlaceholder")}
                value={form.id_interno || ""}
                onChange={(e) => set({ id_interno: maskInternalId(e.target.value) })}
                onBlur={() => {
                  touch("id_interno");
                  setErrors((prev) => ({ ...prev, ...computeErrors({ ...form }) }));
                }}
                fullWidth
                inputProps={{ maxLength: 24, spellCheck: "false" }}
                error={showError("id_interno")}
                helperText={help("id_interno")}
              />
            </Grid>
          </Grid>

          {form.id && (
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2 }}>
              <Tooltip title={getTooltip(form?.data_ultimo_acesso, form?.data_cadastro || form?.data_ultima_alteracao)}>
                <Chip
                  size="small"
                  label={
                    toDateOrNull(form?.data_ultimo_acesso)
                      ? `${t("addUserForm.lastAccessPrefix")}: ${fmtLastAccessLabel(
                          fullLoading,
                          form?.data_ultimo_acesso,
                          form?.data_cadastro || form?.data_ultima_alteracao
                        )}`
                      : fmtLastAccessLabel(
                          fullLoading,
                          form?.data_ultimo_acesso,
                          form?.data_cadastro || form?.data_ultima_alteracao
                        )
                  }
                  variant="outlined"
                  icon={<HistoryRoundedIcon />}
                />
              </Tooltip>

              <Button variant="outlined" onClick={handleSendInvite} disabled={saving}>
                {t("addUserForm.sendInvite")}
              </Button>
              <Button variant="outlined" onClick={handleSendReset} startIcon={<LockResetRoundedIcon />} disabled={saving}>
                {t("addUserForm.sendPasswordReset")}
              </Button>
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* Contato & Acesso */}
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardHeader
          title={t("addUserForm.contactTitle")}
          subheader={t("addUserForm.contactSubheader")}
        />
        <CardContent sx={{ pt: 0 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                type="email"
                label={t("addUserForm.emailLabel")}
                placeholder="nome@empresa.com"
                value={form.email}
                onChange={(e) => set({ email: e.target.value })}
                onBlur={() => {
                  // normaliza
                  set((p) => ({ email: String(p.email || "").trim().toLowerCase() }));
                  touch("email");
                  setErrors((prev) => ({ ...prev, ...computeErrors({ ...form, email: form.email }) }));
                }}
                fullWidth
                autoComplete="email"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailRoundedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                error={showError("email")}
                helperText={help("email")}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label={t("addUserForm.mobileLabel")}
                placeholder="(11) 91234-5678"
                value={form.celular ?? ""}
                onChange={(e) => set({ celular: maskPhoneBR(e.target.value) })}
                onBlur={() => {
                  touch("celular");
                  setErrors((prev) => ({ ...prev, ...computeErrors({ ...form, celular: form.celular }) }));
                }}
                fullWidth
                autoComplete="tel"
                inputMode="numeric"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIphoneRoundedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                error={showError("celular")}
                helperText={help("celular")}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Permissões por Projeto (ACL) */}
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardHeader
          title={
            <Stack direction="row" spacing={1} alignItems="center">
              <SecurityRoundedIcon fontSize="small" />
              <span>{t("addUserForm.aclTitle")}</span>
            </Stack>
          }
          subheader={
            form.id ? t("addUserForm.aclSubheaderEdit") : t("addUserForm.aclSubheaderNew")
          }
        />
        <CardContent sx={{ pt: 0 }}>
          {aclError && form.id && (
            <Box sx={{ mb: 2 }}>
              <Alert severity="error">{aclError}</Alert>
            </Box>
          )}

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={9}>
              <Autocomplete
                multiple
                disableCloseOnSelect
                filterSelectedOptions
                options={projectOptions}
                value={selectedProjects}
                loading={projLoading || aclLoading || fullLoading}
                onChange={(_, val) => setSelectedProjects(val)}
                onInputChange={(_, val) => setProjQuery(val)}
                getOptionLabel={(opt) =>
                  opt?.nome ? `${opt.nome}${opt.codigo_interno ? ` — /${opt.codigo_interno}` : ""}` : ""
                }
                isOptionEqualToValue={(o, v) => Number(o.id) === Number(v.id)}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Avatar sx={{ width: 24, height: 24 }}>
                        {(option.nome || "?").slice(0, 1).toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography fontSize={14} fontWeight={600}>
                          {option.nome || "—"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          /{option.codigo_interno || ""}
                        </Typography>
                      </Box>
                    </Stack>
                  </li>
                )}
                renderTags={(value, getTagProps) =>
                  value.map((opt, idx) => (
                    <Chip {...getTagProps({ index: idx })} key={opt.id} label={opt.nome} variant="outlined" size="small" />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("addUserForm.projectsWithAccessLabel")}
                    placeholder={t("addUserForm.projectsSearchPlaceholder")}
                    helperText={form.id ? t("addUserForm.aclHelperEdit") : t("addUserForm.aclHelperNew")}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {projLoading || aclLoading || fullLoading ? <CircularProgress size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              {form.id ? (
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button variant="contained" startIcon={<SaveRoundedIcon />} onClick={handleSaveAcl} disabled={aclSaving}>
                    {aclSaving ? t("shared.loading") : t("addUserForm.savePermissions")}
                  </Button>
                </Stack>
              ) : (
                <Box />
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Atividade recente (logs_usuarios) */}
      {form.id && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardHeader
            title={t("addUserForm.recentActivityTitle")}
            subheader={t("addUserForm.recentActivitySubheader")}
          />
          <CardContent sx={{ pt: 0 }}>
            {logsError && (
              <Box sx={{ mb: 2 }}>
                <Alert severity="error">{logsError}</Alert>
              </Box>
            )}

            {logsLoading || fullLoading ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={18} /> <span>{t("shared.loading")}</span>
              </Stack>
            ) : logs.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t("addUserForm.noActivity")}
              </Typography>
            ) : (
              <Box sx={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      {[t("addUserForm.whenCol"), t("addUserForm.eventCol")].map((h) => (
                        <th
                          key={h}
                          style={{
                            borderBottom: "1px solid #e0e0e0",
                            padding: 8,
                            textAlign: "left",
                            fontWeight: 600,
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((l) => {
                      const event = String(l.login) === "1" ? "Login" : String(l.logout) === "1" ? "Logout" : "—";
                      return (
                        <tr key={l.id}>
                          <td style={{ padding: 8 }}>{fmtDateTime(l.data_cadastro || l.data_ultima_alteracao)}</td>
                          <td style={{ padding: 8 }}>
                            <Chip
                              size="small"
                              label={event}
                              color={event === "Login" ? "success" : event === "Logout" ? "default" : "warning"}
                              variant="outlined"
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Ações */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
        <Button variant="outlined" onClick={onClose} disabled={isLoading || saving}>
          {t("shared.cancel")}
        </Button>
        <Button type="submit" variant="contained" disabled={isLoading || saving}>
          {isLoading || saving ? <CircularProgress size={20} color="inherit" /> : t("shared.save")}
        </Button>
      </Box>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        message={snack.message}
      />
    </Paper>
  );
}
